import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar/bottom-bar';
import {ElementItemBottomBarOptions, UniqueLineItemBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useAppSelector} from '@/hooks';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  showCopyStylesAsNewFeature,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from './line-bottom-bar.module.scss';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal';
import {openPosterEditorVectorFillModal} from '@Modals/poster-editor-vector-fill-modal';
import {openPosterEditorShadowModal} from '@Modals/poster-editor-item-shadow-modal';
import {openPosterEditorHyperlinkModal} from '@Modals/poster-editor-item-hyperlink-modal';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';
import {VectorItemColorFill} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/vector-item-color-fill/vector-item-color-fill';
import {openPosterEditorLineThicknessModal} from '@Modals/poster-editor-line-thickness-modal';
import {openPosterEditorVectorBorderModal} from '@Modals/poster-editor-vector-border-modal/poster-editor-vector-border-modal-library';
import {openPosterEditorLineRoundedCornersModal} from '@Modals/poster-editor-line-rounded-corners-modal/poster-editor-line-rounded-corners-modal';
import {openPosterEditorLineDashModal} from '@Modals/poster-editor-line-dash-modal/poster-editor-line-dash-modal';
import {openPosterEditorLineCapModal} from '@Modals/poster-editor-line-cap-modal';
import {LineCapStyleOptionTypes} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-cap-style-option/line-cap-style-option.types';
import {openPosterEditorLineColorModal} from '@Modals/poster-editor-line-color-modal/poster-editor-line-color-modal';
export function LineBottomBar(): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;

  if (!activeItem?.isLine()) {
    return null;
  }

  const onClickHandler = (itemId: ElementItemBottomBarOptions | UniqueLineItemBottomBarOptions): void => {
    switch (itemId) {
      case ElementItemBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case ElementItemBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case ElementItemBottomBarOptions.FIll:
        openPosterEditorLineColorModal();
        break;
      case UniqueLineItemBottomBarOptions.THICKNESS:
        openPosterEditorLineThicknessModal();
        break;
      case UniqueLineItemBottomBarOptions.LINE_DASH:
        openPosterEditorLineDashModal();
        break;
      case UniqueLineItemBottomBarOptions.LINE_CAP_START:
        openPosterEditorLineCapModal({
          lineCapStyleOptionType: LineCapStyleOptionTypes.LINE_START,
        });
        break;
      case UniqueLineItemBottomBarOptions.LINE_CAP_END:
        openPosterEditorLineCapModal({
          lineCapStyleOptionType: LineCapStyleOptionTypes.LINE_END,
        });
        break;
      case UniqueLineItemBottomBarOptions.IS_ROUNDED:
        openPosterEditorLineRoundedCornersModal();
        break;
      case ElementItemBottomBarOptions.OUTLINE:
        openPosterEditorVectorBorderModal();
        break;
      case ElementItemBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case ElementItemBottomBarOptions.SHADOW:
        openPosterEditorShadowModal();
        break;
      case ElementItemBottomBarOptions.HYPERLINK:
        openPosterEditorHyperlinkModal();
        break;
      case ElementItemBottomBarOptions.OPACITY:
        openPosterEditorItemOpacityModal();
        break;
      case ElementItemBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      case ElementItemBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case ElementItemBottomBarOptions.DELETE:
        deleteItemByID(activeItem?.uid);
        break;
      default:
        throw new Error('Invalid bottom bar option for line item.');
    }
  };

  const getCustomFillContent = (): ReactElement<any> => {
    return <VectorItemColorFill />;
  };
  const pulsatingDotForCopyStyles = (): ReactElement<any> | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };

  return (
    <BottomBar
      id="poster-editing-rectangle-bottom-bar"
      onClick={onClickHandler}
      items={[
        {
          id: ElementItemBottomBarOptions.FIll,
          customContent: getCustomFillContent(),
          text: window.i18next.t('pmwjs_fill'),
        },
        {
          id: UniqueLineItemBottomBarOptions.THICKNESS,
          icon: 'icon-stroke',
          text: window.i18next.t('pmwjs_thickness'),
        },
        {
          id: UniqueLineItemBottomBarOptions.LINE_DASH,
          icon: 'icon-line-type',
          text: window.i18next.t('pmwjs_line_type'),
        },
        {
          id: UniqueLineItemBottomBarOptions.LINE_CAP_START,
          icon: 'icon-arrow-left',
          text: window.i18next.t('pmwjs_line_start'),
        },
        {
          id: UniqueLineItemBottomBarOptions.LINE_CAP_END,
          icon: 'icon-arrow-right',
          text: window.i18next.t('pmwjs_line_end'),
        },
        {
          id: UniqueLineItemBottomBarOptions.IS_ROUNDED,
          icon: 'icon-corner-top-left',
          text: window.i18next.t('pmwjs_rounded_corners'),
        },
        {
          id: ElementItemBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: ElementItemBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: ElementItemBottomBarOptions.SHADOW,
          icon: 'icon-shadow',
          text: window.i18next.t('pmwjs_shadow'),
        },
        {
          id: ElementItemBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: ElementItemBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: ElementItemBottomBarOptions.HYPERLINK,
          icon: 'icon-link',
          text: window.i18next.t('pmwjs_hyperlink'),
        },
        {
          id: ElementItemBottomBarOptions.OPACITY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: ElementItemBottomBarOptions.LOCK,
          icon: activeItem.fabricObject.lockMovementX ? 'icon-lock' : 'icon-unlock',
          text: activeItem.fabricObject.lockMovementX ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: ElementItemBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
    />
  );
}
